import * as React from 'react';
import { cn } from '../../utils/cn';
import Image from '../../atoms/image';
import type { TextInputProps } from './text-input.types';

/**
 * TextInput Component
 *
 * A customizable text input component with icon support, label, error handling, and multiple size variants.
 * Supports both default and kids-themed styling.
 */
export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
    (
        {
            variant = 'default',
            size = 'large',
            iconLeftProps,
            iconRightProps,
            label,
            error,
            helperText,
            disabled = false,
            required = false,
            testID,
            containerClassName,
            inputClassName,
            labelClassName,
            className,
            onBlur,
            onFocus,
            showIf = true,
            ...props
        },
        ref
    ) => {
        const [_isFocused, setIsFocused] = React.useState(false);

        if (!showIf) return null;

        const handleFocus = () => {
            setIsFocused(true);
            onFocus?.();
        };

        const handleBlur = () => {
            setIsFocused(false);
            onBlur?.();
        };

        const sizeClasses = {
            small: 'h-8 text-sm px-2',
            medium: 'h-10 text-base px-3',
            large: 'h-12 text-base px-4',
        };

        const variantClasses = {
            default: cn(
                'bg-[var(--color-neutral-0)] border border-[var(--color-neutral-30)] rounded-md',
                'focus:outline-none focus:ring-2 focus:ring-[var(--color-info-50)] focus:border-transparent',
                error && 'border-[var(--color-error-5)]0 focus:ring-[var(--color-error-50)]'
            ),
            kids: cn(
                'bg-[var(--color-info-5)] border-2 border-[var(--color-info-20)] rounded-xl',
                'focus:outline-none focus:ring-2 focus:ring-[var(--color-info-40)] focus:border-[var(--color-info-40)]',
                error && 'border-[var(--color-error-40)] focus:ring-[var(--color-error-40)]'
            ),
        };

        const iconSize = {
            small: 'w-4 h-4',
            medium: 'w-5 h-5',
            large: 'w-6 h-6',
        };

        const renderIcon = (iconProps?: typeof iconLeftProps, position?: 'left' | 'right') => {
            if (!iconProps?.icon) return null;

            const isString = typeof iconProps.icon === 'string';
            const IconComponent = iconProps.icon;

            return (
                <button
                    type="button"
                    onClick={iconProps.onPress}
                    disabled={disabled || iconProps.disabled}
                    className={cn(
                        'absolute top-1/2 -translate-y-1/2 flex items-center justify-center',
                        position === 'left' ? 'left-3' : 'right-1',
                        iconProps.onPress && !disabled && !iconProps.disabled && 'cursor-pointer hover:opacity-70',
                        (disabled || iconProps.disabled) && 'opacity-50 cursor-not-allowed',
                        iconProps.className
                    )}
                    tabIndex={-1}
                >
                    {isString ? (
                        <Image
                            src={iconProps.icon as string}
                            alt=""
                            width={24}
                            height={24}
                            className={iconSize[size]}
                        />
                    ) : (
                        <div className={iconSize[size]}>{IconComponent}</div>
                    )}
                </button>
            );
        };

        return (
            <div
                className={cn('flex flex-col gap-1.5', containerClassName)}
                data-testid={testID || 'text-input-container'}
            >
                {/* Label */}
                {label && (
                    <label
                        className={cn(
                            'text-sm font-medium text-[var(--color-neutral-70)]',
                            disabled && 'text-[var(--color-neutral-40)]',
                            error && 'text-[var(--color-error-60)]',
                            labelClassName
                        )}
                    >
                        {label}
                        {required && <span className="text-[var(--color-error-5)]0 ml-1">*</span>}
                    </label>
                )}

                {/* Input Container */}
                <div className="relative">
                    <input
                        ref={ref}
                        disabled={disabled}
                        data-testid={testID || 'text-input'}
                        className={cn(
                            'w-full transition-all duration-200',
                            sizeClasses[size],
                            variantClasses[variant],
                            iconLeftProps?.icon && 'pl-10',
                            iconRightProps?.icon && 'pr-10',
                            disabled &&
                                'bg-[var(--color-neutral-10)] text-[var(--color-neutral-40)] cursor-not-allowed',
                            inputClassName,
                            className
                        )}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        {...props}
                    />

                    {/* Left Icon */}
                    {renderIcon(iconLeftProps, 'left')}

                    {/* Right Icon */}
                    {renderIcon(iconRightProps, 'right')}
                </div>

                {/* Helper Text / Error */}
                {(error || helperText) && (
                    <div
                        className={cn(
                            'text-xs',
                            error ? 'text-[var(--color-error-60)]' : 'text-[var(--color-neutral-5)]0'
                        )}
                    >
                        {error || helperText}
                    </div>
                )}
            </div>
        );
    }
);

TextInput.displayName = 'TextInput';
